import React, { Component, ReactNode } from 'react';
import { logger } from '../../../utils/logger';
import { AlertTriangle, RefreshCw } from 'lucide-react';

interface TableTopicErrorBoundaryProps {
  children: ReactNode;
  onReset?: () => void;
}

interface TableTopicErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
}

/**
 * Error boundary component specifically for TableTopic
 * Catches and handles errors gracefully to prevent full app crashes
 */
export class TableTopicErrorBoundary extends Component<
  TableTopicErrorBoundaryProps,
  TableTopicErrorBoundaryState
> {
  constructor(props: TableTopicErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error): TableTopicErrorBoundaryState {
    // Update state so the next render will show the fallback UI
    logger.error('TableTopic Error Boundary caught error:', error);
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    // Log the error to error reporting service
    logger.error('TableTopic Error Details:', {
      error: error.message,
      stack: error.stack,
      componentStack: errorInfo.componentStack,
    });
  }

  handleReset = () => {
    this.setState({ hasError: false, error: null });
    
    // Call the parent's reset handler if provided
    if (this.props.onReset) {
      this.props.onReset();
    }
  };

  render() {
    if (this.state.hasError) {
      return (
        <div className="h-full flex items-center justify-center bg-gray-50">
          <div className="flex flex-col items-center text-center max-w-md px-4">
            <div className="text-orange-500 mb-4">
              <AlertTriangle size={48} />
            </div>
            <h3 className="text-xl font-bold text-gray-900 mb-2">
              Something went wrong with the table
            </h3>
            <p className="text-gray-600 mb-4">
              {this.state.error?.message || 'An unexpected error occurred while loading the table.'}
            </p>
            <button
              onClick={this.handleReset}
              className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600 transition flex items-center gap-2"
            >
              <RefreshCw size={16} />
              Try again
            </button>
            {process.env.NODE_ENV === 'development' && this.state.error && (
              <details className="mt-4 text-left text-xs text-gray-500 max-w-full overflow-auto">
                <summary className="cursor-pointer hover:text-gray-700">Developer details</summary>
                <pre className="mt-2 p-2 bg-gray-100 rounded overflow-auto max-h-40">
                  {this.state.error.stack}
                </pre>
              </details>
            )}
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}